import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ConfigProvider, Modal, Form, Input, Select, Popover } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined,
  SearchOutlined, DownOutlined, CloseOutlined,
} from "@ant-design/icons";
import useAxiosClient from "../axios-client";
import { useStateContext } from "../contexts/ContextProvider";
import { useDivisions } from "../hooks/useDivisions";
import { useDistricts } from "../hooks/useDistricts";
import { useUpazilas } from "../hooks/useUpazilas";
import { PRIMARY, hexToRgba, SHELL_THEME as THEME, shellAntdTheme, MONO } from "../shellTokens";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const CHANNELS = [
  { label: "All", value: "" },
  { label: "Online", value: "online" },
  { label: "Offline", value: "offline" },
];

/** window width < 768 → mobile card list */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
};

const GRID = "minmax(0,1.8fr) minmax(0,0.7fr) minmax(0,1fr) minmax(0,2fr) minmax(0,0.8fr) 90px";

function Customers() {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();
  const isMobile = useIsMobile();
  const [modal, contextHolder] = Modal.useModal();

  // -- list state --
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [channel, setChannel] = useState("");
  const [downloading, setDownloading] = useState(false);

  // -- create / edit modal --
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [division, setDivision] = useState(null);
  const [district, setDistrict] = useState(null);
  const { divisions, divisionLoading } = useDivisions();
  const { districts, districtLoading } = useDistricts(division);
  const { upazilas, upazilaLoading } = useUpazilas(district);

  // -- open menus --
  const [openRows, setOpenRows] = useState(false);

  const fetchCustomers = useCallback(
    async (nextPage = 1, search = searchText, type = channel) => {
      setLoading(true);
      try {
        let link = `/customers/index?page=${nextPage}`;
        if (search) link += `&search=${encodeURIComponent(search)}`;
        if (type) link += `&type=${type}`;
        const response = await axiosClient.get(link);
        setCustomers(
          (response.data.data || []).map((d) => ({ ...d, key: d.id }))
        );
        setTotal(response.data.total || 0);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Error fetching customers");
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, searchText, channel]
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await axiosClient.get("/customers/stats");
      setStats(res.data);
    } catch {
      // Stats are supplementary — the list still works without them.
    }
  }, [axiosClient]);

  useEffect(() => {
    fetchCustomers(1, searchText, channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSearch = () => {
    setSearchText(searchInput);
    setPage(1);
    fetchCustomers(1, searchInput, channel);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchText("");
    setPage(1);
    fetchCustomers(1, "", channel);
  };

  const changeChannel = (val) => {
    setChannel(val);
    setPage(1);
  };

  const goToPage = (p) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    setPage(Math.max(1, Math.min(totalPages, p)));
    fetchCustomers(p, searchText, channel);
  };

  const changePageSize = (n) => {
    setPageSize(n);
    setPage(1);
    setOpenRows(false);
    fetchCustomers(1, searchText, channel);
  };

  // -- create / edit --
  const openCreate = () => {
    setEditing(null);
    setDivision(null);
    setDistrict(null);
    form.resetFields();
    setFormOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    const divId = record.address?.division?.value ? +record.address.division.value : null;
    const distId = record.address?.district?.value ? +record.address.district.value : null;
    setDivision(divId);
    setDistrict(distId);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone || record.address?.phone,
      facebookId: record.facebook_id,
      altPhone: record.alt_phone,
      address: record.address?.address,
      division: divId,
      district: distId,
      upazila: record.address?.upazila?.value ? +record.address.upazila.value : null,
    });
    setFormOpen(true);
  };

  const onDivisionSelect = (val) => {
    form.setFieldsValue({ district: null, upazila: null });
    setDivision(val);
    setDistrict(null);
  };

  const onDistrictSelect = (val) => {
    form.setFieldsValue({ upazila: null });
    setDistrict(val);
  };

  const saveCustomer = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await axiosClient.post(`/customers/update/${editing.id}`, values);
        toast.success("Customer updated");
      } else {
        await axiosClient.post("/customers/store", values);
        toast.success("Customer created");
      }
      setFormOpen(false);
      fetchCustomers(page, searchText, channel);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error saving customer");
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = (record) =>
    modal.confirm({
      title: `Delete ${record.name}?`,
      content: "This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await axiosClient.delete(`/customers/delete/${record.id}`);
          toast.warning("Customer deleted");
          fetchCustomers(page, searchText, channel);
        } catch (error) {
          toast.error(error?.response?.data?.message || "Error deleting customer");
        }
      },
    });

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const link = `/customers/export${searchText ? `?search=${searchText}` : ""}`;
      const response = await axiosClient.get(link, { responseType: "blob" });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "customers.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("File downloaded successfully.");
    } catch (error) {
      toast.error(error?.message || "Error downloading file");
    } finally {
      setDownloading(false);
    }
  };

  // -- helpers --
  const formatAddress = (address) => {
    if (!address) return null;
    return [address.address, address.upazila?.name || address.upazila, address.district?.name || address.district, address.division?.name || address.division]
      .filter(Boolean)
      .join(", ");
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const showEmpty = !loading && customers.length === 0;
  const hasFilter = searchText || channel;

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [`--${k}`, v])
    ),
    "--primary": PRIMARY,
    "--mono": MONO,
  };

  return (
    <div className="et-customers" style={rootStyle}>
      <style>{`
        .et-customers{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-customers__row:hover{background:var(--hover)}
        .et-customers__card:hover{border-color:var(--borderStrong)}
        .et-customers__ghost:hover{background:var(--hover)}
        .et-customers__skel{background:var(--surface2);border-radius:4px;animation:ovPulse 1.4s ease-in-out infinite}
        @keyframes ovPulse{0%,100%{opacity:.55}50%{opacity:.9}}
      `}</style>

      {/* ---- Header ---- */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
        <div>
          <div style={{ font: "700 22px Outfit, sans-serif", letterSpacing: "-.01em" }}>
            Customers
          </div>
          <div style={{ font: "400 13px Outfit, sans-serif", color: "var(--muted)", marginTop: 2 }}>
            Unified directory — online marketplace buyers and offline walk-in customers.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="et-customers__ghost"
            onClick={downloadExcel}
            disabled={downloading}
            style={ghostBtn}
          >
            <DownloadOutlined style={{ color: "var(--muted)", fontSize: 12 }} /> Export
          </button>
          <button type="button" onClick={openCreate} style={primaryBtn}>
            <PlusOutlined style={{ fontSize: 12 }} /> Offline Customer
          </button>
        </div>
      </div>

      {/* ---- KPI tiles ---- */}
      {stats && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
            gap: 14,
          }}
        >
          <StatTile
            icon="▦"
            tint={PRIMARY}
            label="Total customers"
            value={stats.total}
          />
          <StatTile
            icon="◈"
            tint="#34d399"
            label="Online"
            value={stats.online}
            foot="marketplace buyers"
          />
          <StatTile
            icon="◐"
            tint="#fbbf24"
            label="Offline"
            value={stats.offline}
            foot="walk-in & manual entry"
          />
          <StatTile
            icon="✦"
            tint="#a78bfa"
            label="New this month"
            value={stats.newThisMonth}
          />
        </div>
      )}

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* ---- Toolbar ---- */}
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <div style={{ font: "700 14.5px Outfit, sans-serif" }}>All customers</div>
              <div
                style={{
                  font: "600 11.5px var(--mono)",
                  color: "var(--dim)",
                  padding: "2px 7px",
                  borderRadius: 9,
                  background: "var(--surface2)",
                }}
              >
                {total}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* Channel segmented control — custom styled to match the shell */}
              <div
                style={{
                  display: "flex",
                  padding: 3,
                  borderRadius: 9,
                  background: "var(--surface2)",
                  border: "1px solid var(--border)",
                  gap: 2,
                }}
              >
                {CHANNELS.map((ch) => (
                  <span
                    key={ch.value}
                    onClick={() => changeChannel(ch.value)}
                    style={{
                      height: 28,
                      padding: "0 12px",
                      borderRadius: 7,
                      background: channel === ch.value ? PRIMARY : "transparent",
                      color: channel === ch.value ? "#fff" : "var(--muted)",
                      font: "600 12px Outfit, sans-serif",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    {ch.label}
                  </span>
                ))}
              </div>
              {/* Search */}
              <div
                style={{
                  height: 34,
                  borderRadius: 9,
                  background: "var(--surface2)",
                  border: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "0 10px",
                  width: isMobile ? "100%" : 240,
                }}
              >
                <SearchOutlined style={{ color: "var(--dim)", fontSize: 12 }} />
                <input
                  type="text"
                  placeholder="Search customers…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    font: "400 12.5px Outfit, sans-serif",
                    color: "var(--text)",
                  }}
                />
                {searchInput && (
                  <CloseOutlined
                    onClick={handleClearSearch}
                    style={{ color: "var(--dim)", fontSize: 11, cursor: "pointer" }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ---- Filter chips ---- */}
          {hasFilter && (
            <div
              style={{
                padding: "7px 14px",
                borderBottom: "1px solid var(--border)",
                background: "var(--bg)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  font: "600 11px Outfit, sans-serif",
                  color: "var(--dim)",
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                  marginRight: 2,
                }}
              >
                Filters
              </span>
              {searchText && (
                <Chip
                  label="Search"
                  value={searchText}
                  onClear={handleClearSearch}
                />
              )}
              {channel && (
                <Chip
                  label="Channel"
                  value={channel === "online" ? "Online" : "Offline"}
                  onClear={() => changeChannel("")}
                />
              )}
              <button
                type="button"
                onClick={() => {
                  handleClearSearch();
                  changeChannel("");
                }}
                style={{
                  marginLeft: "auto",
                  height: 26,
                  padding: "0 10px",
                  borderRadius: 7,
                  border: "1px solid var(--border)",
                  background: "transparent",
                  color: "var(--muted)",
                  font: "600 12px Outfit, sans-serif",
                  cursor: "pointer",
                }}
              >
                Clear all
              </button>
            </div>
          )}

          {/* ---- Desktop table ---- */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <div role="table" aria-label="Customers" aria-rowcount={total} style={{ minWidth: 700 }}>
                {/* Header */}
                <div role="rowgroup">
                  <div
                    role="row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: GRID,
                      background: "var(--surface2)",
                      borderBottom: "1px solid var(--border)",
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                    }}
                  >
                    {[
                      { label: "Name", key: "name" },
                      { label: "Channel", key: "channel" },
                      { label: "Phone", key: "phone" },
                      { label: "Address", key: "address" },
                      { label: "Reference", key: "ref" },
                      { label: "", key: "actions", align: "center" },
                    ].map((h) => (
                      <div
                        key={h.key}
                        role="columnheader"
                        style={{
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: h.align === "center" ? "center" : "flex-start",
                          font: "700 11.5px Outfit, sans-serif",
                          color: "var(--muted)",
                          letterSpacing: ".05em",
                          textTransform: "uppercase",
                        }}
                      >
                        {h.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Body */}
                <div role="rowgroup">
                  {loading &&
                    [...Array(6)].map((_, r) => (
                      <div
                        key={r}
                        role="row"
                        style={{
                          display: "grid",
                          gridTemplateColumns: GRID,
                          borderBottom: "1px solid var(--border)",
                          minHeight: 50,
                          alignItems: "center",
                        }}
                      >
                        {[...Array(6)].map((_, c) => (
                          <div key={c} role="gridcell" style={{ padding: "0 8px" }}>
                            <div
                              className="et-customers__skel"
                              style={{ height: 10, width: `${55 + (c * 7) % 30}%` }}
                            />
                          </div>
                        ))}
                      </div>
                    ))}

                  {showEmpty && (
                    <div
                      style={{
                        padding: "44px 18px",
                        textAlign: "center",
                        font: "400 12.5px Outfit, sans-serif",
                        color: "var(--muted)",
                      }}
                    >
                      {hasFilter
                        ? "No customers match your filters — try clearing them."
                        : "No customers yet — add the first offline customer."}
                    </div>
                  )}

                  {!loading &&
                    customers.map((c) => {
                      const addr = c.address;
                      const phone = c.phone || addr?.phone || c.alt_phone;
                      return (
                        <div
                          key={c.id}
                          className="et-customers__row"
                          role="row"
                          style={{
                            display: "grid",
                            gridTemplateColumns: GRID,
                            borderBottom: "1px solid var(--border)",
                            minHeight: 50,
                            alignItems: "center",
                          }}
                        >
                          {/* Name */}
                          <div
                            role="gridcell"
                            style={{
                              padding: "9px 8px",
                              display: "flex",
                              alignItems: "center",
                              gap: 9,
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                flex: "none",
                                width: 30,
                                height: 30,
                                borderRadius: 8,
                                background: c.type === "offline" ? "rgba(251,191,36,.16)" : hexToRgba(PRIMARY, .16),
                                color: c.type === "offline" ? "#fbbf24" : "var(--activeText)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              {(c.name || "?")[0].toUpperCase()}
                            </span>
                            <span
                              style={{
                                font: "600 13px Outfit, sans-serif",
                                color: "var(--text)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.name}
                            </span>
                          </div>

                          {/* Channel */}
                          <div role="gridcell" style={{ padding: "9px 8px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                font: "600 10.5px Outfit, sans-serif",
                                padding: "2px 8px",
                                borderRadius: 6,
                                background: c.type === "offline" ? "rgba(251,191,36,.14)" : hexToRgba(PRIMARY, .14),
                                color: c.type === "offline" ? "#b88a1c" : "var(--activeText)",
                              }}
                            >
                              {c.type === "offline" ? "Offline" : "Online"}
                            </span>
                          </div>

                          {/* Phone */}
                          <div role="gridcell" style={{ padding: "9px 8px" }}>
                            <span
                              style={{
                                font: "400 12.5px var(--mono)",
                                color: phone ? "var(--text)" : "var(--dim)",
                              }}
                            >
                              {phone || "—"}
                            </span>
                          </div>

                          {/* Address */}
                          <div role="gridcell" style={{ padding: "9px 8px", minWidth: 0 }}>
                            <span
                              style={{
                                font: "400 12.5px Outfit, sans-serif",
                                color: addr ? "var(--text)" : "var(--dim)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                              }}
                              title={formatAddress(addr)}
                            >
                              {formatAddress(addr) || "—"}
                            </span>
                          </div>

                          {/* Reference */}
                          <div role="gridcell" style={{ padding: "9px 8px" }}>
                            <span
                              style={{
                                font: "400 12.5px Outfit, sans-serif",
                                color: c.facebook_id ? "var(--text)" : "var(--dim)",
                              }}
                            >
                              {c.facebook_id || "—"}
                            </span>
                          </div>

                          {/* Actions */}
                          <div
                            role="gridcell"
                            style={{
                              padding: "9px 8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                            }}
                          >
                            {c.type === "offline" ? (
                              <>
                                <button
                                  type="button"
                                  className="et-customers__ghost"
                                  title="Edit"
                                  onClick={() => openEdit(c)}
                                  style={iconBtn}
                                >
                                  <EditOutlined style={{ fontSize: 12 }} />
                                </button>
                                <button
                                  type="button"
                                  className="et-customers__ghost"
                                  title="Delete"
                                  onClick={() => deleteCustomer(c)}
                                  style={{
                                    ...iconBtn,
                                    color: "var(--dim)",
                                  }}
                                >
                                  <DeleteOutlined style={{ fontSize: 12 }} />
                                </button>
                              </>
                            ) : (
                              <span style={{ font: "400 11px Outfit, sans-serif", color: "var(--dim)" }}>
                                —
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* ---- Mobile cards ---- */}
          {isMobile && (
            <div
              role="list"
              aria-label="Customers"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: 14,
                display: "flex",
                flexDirection: "column",
                gap: 9,
              }}
            >
              {loading &&
                [...Array(5)].map((_, r) => (
                  <div
                    key={r}
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      padding: "13px 14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 9,
                    }}
                  >
                    <div className="et-customers__skel" style={{ height: 12, width: "55%" }} />
                    <div className="et-customers__skel" style={{ height: 12, width: "80%" }} />
                    <div className="et-customers__skel" style={{ height: 10, width: "40%" }} />
                  </div>
                ))}

              {showEmpty && (
                <div
                  style={{
                    padding: "44px 18px",
                    textAlign: "center",
                    font: "400 12.5px Outfit, sans-serif",
                    color: "var(--muted)",
                  }}
                >
                  {hasFilter
                    ? "No customers match your filters."
                    : "No customers yet — add the first offline customer."}
                </div>
              )}

              {!loading &&
                customers.map((c) => {
                  const addr = c.address;
                  const phone = c.phone || addr?.phone || c.alt_phone;
                  return (
                    <div
                      key={c.id}
                      className="et-customers__card"
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 12,
                        padding: "12px 13px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <span
                          style={{
                            flex: "none",
                            width: 34,
                            height: 34,
                            borderRadius: 9,
                            background:
                              c.type === "offline"
                                ? "rgba(251,191,36,.16)"
                                : hexToRgba(PRIMARY, .16),
                            color: c.type === "offline" ? "#fbbf24" : "var(--activeText)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {(c.name || "?")[0].toUpperCase()}
                        </span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              font: "600 14px Outfit, sans-serif",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.name}
                          </div>
                          <div
                            style={{
                              font: "400 11.5px var(--mono)",
                              color: "var(--dim)",
                              marginTop: 2,
                            }}
                          >
                            {phone || "No phone"}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span
                          style={{
                            font: "600 10.5px Outfit, sans-serif",
                            padding: "2px 8px",
                            borderRadius: 6,
                            background:
                              c.type === "offline"
                                ? "rgba(251,191,36,.14)"
                                : hexToRgba(PRIMARY, .14),
                            color: c.type === "offline" ? "#b88a1c" : "var(--activeText)",
                          }}
                        >
                          {c.type === "offline" ? "Offline" : "Online"}
                        </span>
                        {c.facebook_id && (
                          <span
                            style={{
                              font: "400 11px Outfit, sans-serif",
                              color: "var(--muted)",
                            }}
                          >
                            {c.facebook_id}
                          </span>
                        )}
                      </div>

                      {addr && formatAddress(addr) && (
                        <div
                          style={{
                            font: "400 11.5px Outfit, sans-serif",
                            color: "var(--muted)",
                            lineHeight: 1.4,
                          }}
                        >
                          {formatAddress(addr)}
                        </div>
                      )}

                      {c.type === "offline" && (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            borderTop: "1px solid var(--border)",
                            paddingTop: 10,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => openEdit(c)}
                            style={mobileActionBtn}
                          >
                            <EditOutlined style={{ fontSize: 12 }} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteCustomer(c)}
                            style={{
                              ...mobileActionBtn,
                              color: "var(--dim)",
                              borderColor: "transparent",
                            }}
                          >
                            <DeleteOutlined style={{ fontSize: 12 }} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {/* ---- Pagination ---- */}
          <div
            style={{
              padding: "12px 18px",
              borderTop: "1px solid var(--border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ font: "400 12px Outfit, sans-serif", color: "var(--muted)" }}>
                {total === 0 ? (
                  "0 results"
                ) : (
                  <>
                    Showing{" "}
                    <span style={{ fontFamily: "var(--mono)", color: "var(--text)" }}>
                      {rangeStart}–{rangeEnd}
                    </span>{" "}
                    of{" "}
                    <span style={{ fontFamily: "var(--mono)", color: "var(--text)" }}>
                      {total}
                    </span>
                  </>
                )}
              </div>
              <Popover
                trigger="click"
                open={openRows}
                onOpenChange={setOpenRows}
                content={
                  <MenuList
                    items={PAGE_SIZE_OPTIONS.map((n) => ({ key: n, label: String(n) }))}
                    active={pageSize}
                    onSelect={changePageSize}
                  />
                }
              >
                <span style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
                  <span style={{ font: "400 12px Outfit, sans-serif", color: "var(--muted)" }}>
                    Rows
                  </span>
                  <span
                    style={{
                      height: 28,
                      padding: "0 9px",
                      borderRadius: 7,
                      border: "1px solid var(--border)",
                      background: "var(--surface2)",
                      color: "var(--text)",
                      font: "600 12px var(--mono)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {pageSize}
                    <DownOutlined style={{ fontSize: 9, color: "var(--muted)" }} />
                  </span>
                </span>
              </Popover>
            </div>

            {isMobile ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                  style={{ ...pagerBtn, flex: 1, opacity: page <= 1 ? 0.5 : 1 }}
                >
                  ‹ Prev
                </button>
                <span
                  style={{
                    height: 38,
                    padding: "0 12px",
                    borderRadius: 9,
                    background: "var(--surface2)",
                    border: "1px solid var(--border)",
                    font: "600 12.5px var(--mono)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => goToPage(page + 1)}
                  style={{
                    ...pagerBtn,
                    flex: 1,
                    background: page >= totalPages ? "var(--surface2)" : PRIMARY,
                    borderColor: page >= totalPages ? "var(--border)" : PRIMARY,
                    color: page >= totalPages ? "var(--dim)" : "#fff",
                  }}
                >
                  Next ›
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <PageBtn disabled={page <= 1} onClick={() => goToPage(page - 1)}>
                  ‹
                </PageBtn>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && (
                        <span style={{ color: "var(--dim)", fontSize: 12 }}>…</span>
                      )}
                      <PageBtn active={p === page} onClick={() => goToPage(p)}>
                        {p}
                      </PageBtn>
                    </React.Fragment>
                  ))}
                <PageBtn disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>
                  ›
                </PageBtn>
              </div>
            )}
          </div>
        </div>

        {/* ---- Create / Edit Modal ---- */}
        <Modal
          title={editing ? "Edit customer" : "New offline customer"}
          open={formOpen}
          onCancel={() => setFormOpen(false)}
          onOk={() => form.submit()}
          okText={editing ? "Save" : "Create"}
          confirmLoading={saving}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={saveCustomer} className="mt-2">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Enter a name" }]}
              >
                <Input placeholder="Customer name" />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: "Enter a phone" }]}
              >
                <Input placeholder="Phone number" />
              </Form.Item>
              <Form.Item name="facebookId" label="Reference">
                <Input placeholder="Reference no." />
              </Form.Item>
              <Form.Item name="altPhone" label="Alternative phone">
                <Input placeholder="Optional" />
              </Form.Item>
            </div>
            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true, message: "Enter an address" }]}
            >
              <Input.TextArea rows={1} placeholder="House, road, area..." />
            </Form.Item>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
              <Form.Item
                name="division"
                label="Division"
                rules={[{ required: true, message: "Select division" }]}
              >
                <Select
                  loading={divisionLoading}
                  onSelect={onDivisionSelect}
                  placeholder="Division"
                >
                  {divisions.map((d) => (
                    <Select.Option value={d.id} key={d.id}>
                      {d.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="district"
                label="District"
                rules={[{ required: true, message: "Select district" }]}
              >
                <Select
                  loading={districtLoading}
                  onSelect={onDistrictSelect}
                  placeholder="District"
                >
                  {districts.map((d) => (
                    <Select.Option value={d.id} key={d.id}>
                      {d.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="upazila" label="Upazila / Thana">
                <Select loading={upazilaLoading} placeholder="Upazila">
                  {upazilas.map((d) => (
                    <Select.Option value={d.id} key={d.id}>
                      {d.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </Form>
        </Modal>
        {contextHolder}
      </ConfigProvider>
    </div>
  );
}

/* ==========================================================================
   Shared primitives — same as Orders / ReadyStock
   ========================================================================== */

const ghostBtn = {
  height: 34,
  padding: "0 13px",
  borderRadius: 9,
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--muted)",
  font: "600 12px Outfit, sans-serif",
  display: "flex",
  alignItems: "center",
  gap: 7,
  cursor: "pointer",
};

const primaryBtn = {
  height: 34,
  padding: "0 13px",
  borderRadius: 9,
  border: "none",
  background: PRIMARY,
  color: "#fff",
  font: "600 12px Outfit, sans-serif",
  display: "flex",
  alignItems: "center",
  gap: 7,
  cursor: "pointer",
};

const iconBtn = {
  width: 30,
  height: 30,
  borderRadius: 7,
  border: "none",
  background: "transparent",
  color: "var(--muted)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
};

const mobileActionBtn = {
  height: 34,
  padding: "0 14px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--muted)",
  font: "600 12px Outfit, sans-serif",
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
};

const pagerBtn = {
  height: 38,
  padding: "0 14px",
  borderRadius: 9,
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--text)",
  font: "600 12px Outfit, sans-serif",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

/* ---- Reusable sub-components ---- */

function Chip({ label, value, tint, mono, onClear }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        height: 26,
        padding: "0 9px",
        borderRadius: 7,
        background: tint ? `${tint}18` : "var(--surface2)",
        border: `1px solid ${tint ? `${tint}40` : "var(--border)"}`,
        font: "400 11.5px Outfit, sans-serif",
        color: tint || "var(--text)",
      }}
    >
      {label && (
        <span style={{ fontWeight: 600, color: "var(--dim)", marginRight: 1 }}>
          {label}
        </span>
      )}
      <span
        style={{
          fontFamily: mono ? "var(--mono)" : "Outfit, sans-serif",
          fontWeight: 500,
        }}
      >
        {value}
      </span>
      <span
        onClick={onClear}
        style={{
          width: 15,
          height: 15,
          borderRadius: "50%",
          background: "rgba(255,255,255,.1)",
          color: "var(--dim)",
          fontSize: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        ✕
      </span>
    </span>
  );
}

function PageBtn({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        border: active ? "1px solid var(--primary)" : "1px solid transparent",
        background: active ? "var(--activeBg)" : "transparent",
        color: active ? "var(--activeText)" : "var(--muted)",
        font: active ? "700 12.5px var(--mono)" : "500 12.5px var(--mono)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

function StatTile({ icon, tint, label, value, foot }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span
          style={{
            width: 19,
            height: 19,
            borderRadius: 6,
            background: `${tint}24`,
            color: tint,
            fontSize: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </span>
        <span
          style={{
            font: "600 11.5px Outfit, sans-serif",
            color: "var(--muted)",
            textTransform: "uppercase",
            letterSpacing: ".02em",
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          font: "700 18px var(--mono)",
          color: "var(--text)",
        }}
      >
        {value != null ? value.toLocaleString() : "—"}
      </div>
      {foot && (
        <div
          style={{
            font: "400 11px Outfit, sans-serif",
            color: "var(--dim)",
          }}
        >
          {foot}
        </div>
      )}
    </div>
  );
}

function MenuList({ items, active, onSelect, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {items.map((item) => {
        const isActive = item.key === active;
        return (
          <div
            key={item.key}
            onClick={() => onSelect(item.key)}
            style={{
              padding: "6px 14px",
              borderRadius: 7,
              cursor: "pointer",
              background: isActive ? "var(--activeBg)" : "transparent",
              color: isActive ? "var(--activeText)" : "var(--text)",
              font: "400 12.5px Outfit, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
          </div>
        );
      })}
      {hint && (
        <div
          style={{
            padding: "6px 14px",
            font: "400 11.5px Outfit, sans-serif",
            color: "var(--dim)",
            whiteSpace: "nowrap",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

export default Customers;
